import { useContext } from 'react'
import { useTranslation } from 'libs/i18n'
import { NavLink, useLocation } from 'libs/router'
// todo: move icons to libs/icons
import {
	ArrowRightOnRectangleIcon,
	ArrowTopRightOnSquareIcon,
	BookOpenIcon,
	BuildingStorefrontIcon,
	ChartBarIcon,
	Cog6ToothIcon,
	CubeIcon,
	ShoppingBagIcon,
	UsersIcon,
	WalletIcon,
	XMarkIcon
} from '@heroicons/react/24/outline'

import { AuthContext, DashboardContext, StoreSettingsContext, NavContext } from 'store'
import { useUserRoles, useLogout } from 'shared/hooks'
import { LanguageSwitcher, Currency, StoreBrand } from 'shared/components'
import { NavItem } from './NavItem'
import { DropdownItem, isDropdownItemActive } from './DropdownItem'
import { useProductSettingsTabs } from '../ProductSettingsTabs/hooks/useProductSettingsTabs'
import { useBookingEngineTabs } from '../BookingEngineTabs/hooks/useBookingEngineTabs'
import { Avatar } from 'shared/atoms'

import type { IDropdownItem } from './types'
import { cn } from 'utils/style'
import { Currency as CurrencyType } from 'shared/types/general.types'

const footerItemClassName =
	'mt-1 flex w-full items-center gap-3 rounded-md px-3 py-2 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'

export const Sidebar = () => {
	const { t } = useTranslation()
	const { pathname } = useLocation()
	const navCtx = useContext(NavContext)
	const settingsCtx = useContext(StoreSettingsContext)
	const dashboardCtx = useContext(DashboardContext)
	const { auth } = useContext(AuthContext)
	const { isUser } = useUserRoles()
	const productSettingsTabs = useProductSettingsTabs()
	const bookingEngineTabs = useBookingEngineTabs()

	const logout = useLogout()
	const handleLogout = () => logout()

	const closeSidebar = () => {
		navCtx?.toggleNavOpened(false)
	}

	const userName: string | undefined = auth?.user?.name || auth?.user?.email

	const settingsItems: IDropdownItem[] = [
		{
			to: '/store',
			label: t('Account')
		},
		{
			to: '/categories',
			label: t('Products'),
			activePaths: productSettingsTabs.map((tab) => tab.value)
		},
		{
			to: '/coupons',
			label: t('Booking engine'),
			activePaths: bookingEngineTabs.map((tab) => tab.value)
		}
	]

	const isSettingsDropdownActive = settingsItems.some((item) =>
		isDropdownItemActive(item, pathname)
	)

	return (
		<>
			{navCtx?.isNavOpened && (
				<div className="fixed inset-0 z-40 bg-overlay xl:hidden" onClick={closeSidebar} />
			)}
			<aside
				className={cn(
					'fixed bottom-0 left-0 top-0 z-50 flex w-64 flex-col overflow-y-auto border-r border-border bg-card transition-[transform,visibility] xl:visible xl:translate-x-0',
					navCtx?.isNavOpened ? 'visible translate-x-0' : 'invisible -translate-x-full'
				)}>
				<div className="relative flex min-h-[112px] shrink-0 items-center justify-center border-b border-border px-9 pb-6 pt-11 xl:pt-6">
					<StoreBrand size="lg" className="justify-center" />
					<button
						type="button"
						aria-label={t('Close menu')}
						className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground xl:hidden"
						onClick={closeSidebar}>
						<XMarkIcon className="h-5 w-5" />
					</button>
				</div>

				<nav className="flex-1 px-3 py-5">
					<p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
						{t('Main menu')}
					</p>
					<div className="space-y-1">
						<NavItem
							to="/"
							text={t('Dashboard')}
							onClick={closeSidebar}
							icon={<ChartBarIcon />}
							end
						/>
						<NavItem
							to="/sales"
							text={t('Sales')}
							onClick={closeSidebar}
							icon={<ShoppingBagIcon />}
						/>
						<NavItem
							to="/products"
							text={t('Products')}
							onClick={closeSidebar}
							icon={<CubeIcon />}
						/>
						<NavItem
							to="/bookings"
							text={t('Bookings')}
							onClick={closeSidebar}
							icon={<BookOpenIcon />}
						/>
						<NavItem
							to="/payouts"
							text={t('Payouts')}
							onClick={closeSidebar}
							icon={<WalletIcon />}
						/>

						{!isUser && (
							<NavItem to="/users" text={t('Users')} onClick={closeSidebar} icon={<UsersIcon />} />
						)}

						{!isUser && (
							<DropdownItem
								icon={<Cog6ToothIcon />}
								labelText={t('Settings')}
								items={settingsItems}
								isActive={isSettingsDropdownActive}
							/>
						)}
					</div>
				</nav>

				<div className="border-t border-border p-3">
					<div className="mb-2 rounded-md bg-success-soft p-3">
						<div className="flex items-center gap-2 text-[11px] font-medium text-muted-foreground">
							<WalletIcon className="h-4 w-4 stroke-2 text-success" />
							{t('Account balance')}
						</div>
						<Currency
							className="mt-1 block font-display text-[15px] font-bold"
							price={{
								amount: dashboardCtx?.dashboard?.balance as number,
								currency: dashboardCtx?.dashboard?.currency as CurrencyType
							}}
						/>
					</div>

					<NavLink
						to="/profile"
						className={({ isActive }) =>
							cn(
								'flex w-full items-center gap-3 rounded-md px-3 py-2 transition-colors hover:bg-muted sm:hidden',
								isActive && 'bg-muted'
							)
						}
						onClick={closeSidebar}>
						<Avatar name={userName} indicator={!auth?.user?.payout_method} />
						<div className="min-w-0 flex-1">
							<p className="truncate text-[12px] font-semibold">{userName}</p>
							<p className="text-[10px] text-muted-foreground">{t('Profile')}</p>
						</div>
					</NavLink>

					{settingsCtx?.storeSettings?.website_url && (
						<a
							href={settingsCtx?.storeSettings?.website_url}
							target="_blank"
							rel="noreferrer"
							className={footerItemClassName}>
							<BuildingStorefrontIcon className="h-4 w-4 shrink-0 stroke-2" />
							<span className="truncate">{t('Visit store')}</span>
							<ArrowTopRightOnSquareIcon className="ml-auto h-3.5 w-3.5 shrink-0 stroke-2" />
						</a>
					)}
					<LanguageSwitcher variant="sidebar" />
					<button type="button" className={footerItemClassName} onClick={handleLogout}>
						<ArrowRightOnRectangleIcon className="h-4 w-4 shrink-0 stroke-2" />
						<span>{t('Logout')}</span>
					</button>
					<a
						href="https://smartgo.is"
						className="block px-3 pt-4 text-[10px] text-muted-foreground transition-colors hover:text-foreground">
						{t('Powered by Smartgo')}
					</a>
				</div>
			</aside>
		</>
	)
}
